import { v4 as uuidv4 } from "uuid"

import { PluginApprovalStatus } from "@fuse/recoil/plugins"
import {
  NEW_PLUGIN_ID_PREFIX,
  Plugin,
  UserRole,
} from "@fuse/recoil/pluginStudio/types"

import { getEffectivePluginConfig } from "@settingsAlias/pluginDefaults"

import { ExamplePlugin } from "./examplePlugins"

/**
 * Patterns that indicate a zero-config host config view
 */
const ZERO_CONFIG_PATTERNS = [
  // Direct zero-config indicators
  /zero\s+configuration\s+required/i,
  /no\s+setup\s+needed/i,
  /no\s+configuration\s+required/i,
  /automatically\s+configured/i,

  // Simple static content patterns
  /return\s*\(\s*<[^>]*>\s*<[^>]*>\s*.*zero.*config.*<\/[^>]*>\s*<\/[^>]*>\s*\)/i,

  // Minimal component patterns (just displays a message, no form elements)
  /^[^{]*function[^{]*\{[^}]*return[^}]*<[^>]*>[^<]*(?:zero|no.*config|automatic)[^<]*<\/[^>]*>[^}]*\}[^}]*$/i,
]

/**
 * Patterns that indicate a plugin requires configuration
 */
const REQUIRES_CONFIG_PATTERNS = [
  // Form elements
  /<input[^>]*type=["'](?:text|email|password|number|checkbox|radio)/i,
  /<textarea/i,
  /<select/i,
  /useState\s*\(\s*\{/i, // State with object (likely config)

  // Configuration-related functions
  /saveConfig|loadConfig|handleConfigChange/i,
  /pluginApi\.savePluginConfig/i,
  /pluginApi\.getPluginConfig/i,

  // Multiple form fields
  /(?:<input|<textarea|<select)[^>]*>.*(?:<input|<textarea|<select)/is,
]

/**
 * Analyzes host config view content to determine if it's zero-config
 */
export const analyzeHostConfigForZeroConfig = (
  hostConfigContent: string,
): boolean => {
  if (!hostConfigContent || hostConfigContent.trim() === "") {
    return true // Empty config view = zero config
  }

  // Remove comments and strings to avoid false positives
  const cleanContent = hostConfigContent
    .replace(/\/\*[\s\S]*?\*\//g, "") // Remove block comments
    .replace(/\/\/.*$/gm, "") // Remove line comments
    .replace(/["'`](?:[^"'`\\]|\\.)*["'`]/g, '""') // Replace strings with empty strings

  // Check for zero-config patterns
  const hasZeroConfigPattern = ZERO_CONFIG_PATTERNS.some((pattern) =>
    pattern.test(cleanContent),
  )

  // Check for configuration patterns
  const hasConfigPattern = REQUIRES_CONFIG_PATTERNS.some((pattern) =>
    pattern.test(cleanContent),
  )

  // If it has zero-config patterns and no config patterns, it's zero-config
  if (hasZeroConfigPattern && !hasConfigPattern) {
    return true
  }

  // If it has config patterns, it's not zero-config
  if (hasConfigPattern) {
    return false
  }

  // If the content is very minimal (less than 500 chars of actual code), likely zero-config
  const codeContent = cleanContent
    .replace(/\s+/g, " ") // Normalize whitespace
    .replace(/import[^;]+;/g, "") // Remove imports
    .replace(/export\s+default\s+function[^{]*\{/, "") // Remove function declaration
    .replace(/\}[^}]*$/, "") // Remove closing brace
    .trim()

  if (codeContent.length < 500) {
    return true
  }

  // Default to requiring configuration if we can't determine
  return false
}

/**
 * Converts an example plugin to the Plugin Studio format
 */
export const convertExampleToPlugin = (example: ExamplePlugin): Plugin => {
  const timestamp = Date.now()
  const pluginId = `${NEW_PLUGIN_ID_PREFIX}${timestamp}`
  const config = getEffectivePluginConfig()

  // Analyze host config view for zero-config detection
  const hostConfigContent = example.files[UserRole.HOST_CONFIG_VIEW]
  const isZeroConfigDetected = config.ENABLE_AUTO_ZERO_CONFIG_DETECTION
    ? analyzeHostConfigForZeroConfig(hostConfigContent)
    : false

  // Determine if this should be marked as zero-config
  const shouldBeZeroConfig =
    config.AUTO_MARK_EXAMPLES_AS_ZERO_CONFIG || isZeroConfigDetected

  console.log(`[convertExampleToPlugin] Processing ${example.name}:`, {
    autoMarkAsZeroConfig: config.AUTO_MARK_EXAMPLES_AS_ZERO_CONFIG,
    enableAutoDetection: config.ENABLE_AUTO_ZERO_CONFIG_DETECTION,
    isZeroConfigDetected,
    shouldBeZeroConfig,
    defaultApprovalStatus: config.DEFAULT_APPROVAL_STATUS,
  })

  return {
    id: pluginId,
    name: example.name,
    description: example.description,
    manifest: {
      name: example.name,
      description: example.description,
      version: "1.0.0",
      author: "Plugin Studio User",
      category: example.category.toLowerCase(),
      tags: example.tags,
      // Set zero-config and approval status based on configuration
      requiresZeroConfiguration: shouldBeZeroConfig,
      approvalStatus: config.DEFAULT_APPROVAL_STATUS,
      iconName: example.iconName || "lightning",
      showHostViewInDetails: example.showHostViewInDetails,
      requiresJoinToView: example.requiresJoinToView,
    },
    files: [
      {
        id: uuidv4(),
        name: "GuestView.tsx",
        path: `/frontend/GuestView.tsx`,
        content: example.files[UserRole.GUEST],
        type: UserRole.GUEST,
        lastModified: timestamp,
      },
      {
        id: uuidv4(),
        name: "HostEventView.tsx",
        path: `/frontend/HostEventView.tsx`,
        content: example.files[UserRole.HOST_EVENT_VIEW],
        type: UserRole.HOST_EVENT_VIEW,
        lastModified: timestamp,
      },
      {
        id: uuidv4(),
        name: "HostConfigView.tsx",
        path: `/frontend/HostConfigView.tsx`,
        content: example.files[UserRole.HOST_CONFIG_VIEW],
        type: UserRole.HOST_CONFIG_VIEW,
        lastModified: timestamp,
      },
    ],
    code: {
      frontendGuestCodeWeb: example.files[UserRole.GUEST],
      frontendHostViewCodeWeb: example.files[UserRole.HOST_EVENT_VIEW],
      frontendHostConfigCodeWeb: example.files[UserRole.HOST_CONFIG_VIEW],
    },
    createdAt: timestamp,
    isNew: true,
  }
}
